let drawerSteps = [];

window.LoopUI = {
  // Card Components
  cards: {
    yourDogsSkeleton: ({ dogCount = 2 } = {}) => {
      const card = window.LoopUtils.createElementWithClasses("div", [
        "loop-card",
      ]);

      // Dogs are fetched from a remote DB, so hold the layout with skeleton
      // placeholders instead of an empty container / bare spinner.
      const skeletonDogs = Array.from(
        { length: dogCount },
        () =>
          `<div class="loop-dog-image loop-dog-image-small loop-skeleton loop-skeleton-dog-image"></div>`,
      ).join("");

      card.innerHTML = `
        <div class="loop-card-body">
          <div class="flex justify-content-between mb-3">
            <div class="loop-h3 white-space-break-spaces line-height-normal">${window.t(
              "dogs.yourDogs",
            )}</div>
            <button disabled class="flex justify-center white-space-nowrap max-height-min-content btn-full-round-outline text-sm bold py-2">${window.t(
              "ui.see",
            )}</button>
          </div>
          <div class="loop-dog-image-card-container loop-dog-image-card-container-loading">
            ${skeletonDogs}
          </div>
        </div>
      `;

      return card;
    },

    addNewDogButton: () => {
      const addNewDogButton = window.LoopUtils.createElementWithClasses(
        "button",
        ["loop-dog-card", "loop-add-dog-card"],
      );
      addNewDogButton.type = "button";
      addNewDogButton.innerHTML = `
        <span class="loop-add-dog-label">${window.t("dogs.addNewDog")}</span>
        <span class="loop-add-dog-icon">${window.LoopSVG.plusSvg()}</span>
      `;
      addNewDogButton.addEventListener("click", async () => {
        const newDogContent = await window.LoopUI.drawerContent.newDog();
        window.LoopUI.drawer.replaceDrawerInfo({
          title: newDogContent.title,
          content: newDogContent.content,
          shouldShowBack: true,
        });
      });

      return addNewDogButton;
    },

    dogBodyDrawer: async (dog) => {
      const dogContent = await window.LoopUI.drawerContent.editDog(dog);

      if (dogContent && dogContent.content) {
        window.LoopUI.drawer.replaceDrawerInfo({
          title: dogContent.title,
          content: dogContent.content,
          shouldShowBack: true,
        });
      }
    },

    deleteDogConfirmationDrawer: async (dog) => {
      const data = await window.LoopUI.drawerContent.deleteDogConfirmation(dog);

      if (data && data.content) {
        window.LoopUI.drawer.replaceDrawerInfo({
          title: data.title,
          content: data.content,
          shouldShowBack: true,
        });
      }
    },

    /**
     * Inline error line for the dog forms. The dogs API rejects an invalid
     * body with 400, so a failed save has to say something — otherwise the
     * button just looks broken.
     */
    dogFormError: () => {
      const element = window.LoopUtils.createElementWithClasses("p", [
        "loop-dog-form-error",
      ]);
      element.setAttribute("role", "alert");
      element.hidden = true;

      return {
        element,
        clear: () => {
          element.textContent = "";
          element.hidden = true;
        },
        show: (error) => {
          // Say something the user can act on: which field is missing, or that
          // the Clerk session is gone — not just "something failed".
          let message = window.t("dogs.saveError");

          if (error?.missingFields?.length) {
            const labels = error.missingFields
              .map((field) =>
                window
                  .t(`dogs.${{ life_stage: "lifeStage" }[field] || field}`)
                  .toLowerCase(),
              )
              .join(", ");
            message = window.t("dogs.requiredFields", labels);
          } else if (error?.status === 401) {
            message = window.t("dogs.sessionExpired");
          } else if (error?.status === 409) {
            // WEB-371: the customer already has a dog with this name (core 409
            // DOG_NAME_ALREADY_EXISTS). Retrying never helps, so say why.
            message = window.t("dogs.duplicateName");
          }

          element.textContent = message;
          element.hidden = false;
        },
      };
    },

    /**
     * Dog avatar: the profile photo when there is one, otherwise the coloured
     * paw placeholder.
     */
    dogAvatarMarkup: (dog, { sizeClass = "", extraClasses = "" } = {}) => {
      const photoUrl = window.LoopUtils.getDogPhotoUrl(dog);
      const classes = [
        "loop-dog-image",
        sizeClass,
        extraClasses,
        photoUrl
          ? "loop-dog-image-has-photo"
          : window.LoopUtils.getDogRandomClass(),
      ]
        .filter(Boolean)
        .join(" ");

      const inner = photoUrl
        ? `<img class="loop-dog-photo" src="${window.LoopUtils.escapeHtml(
            photoUrl,
          )}" alt="${window.LoopUtils.escapeHtml(dog?.name)}" loading="lazy" />`
        : window.LoopSVG.dogImage();

      return `<div class="${classes}">${inner}</div>`;
    },

    dogCard: (dog) => {
      const card = window.LoopUtils.createElementWithClasses("div", [
        "loop-dog-card",
      ]);

      card.addEventListener("click", () => {
        window.LoopUI.cards.dogBodyDrawer(dog);
      });

      const meta = [
        window.LoopUtils.getDogGenderLabel(dog),
        window.LoopUtils.getDogLifeStageLabel(dog),
        dog.weight ? `${dog.weight} kg` : "",
      ]
        .filter(Boolean)
        .map(
          (value) =>
            `<span class="loop-dog-meta-item">${window.LoopUtils.escapeHtml(
              value,
            )}</span>`,
        )
        .join(`<span class="loop-dog-meta-separator">·</span>`);

      card.innerHTML = `
        ${window.LoopUI.cards.dogAvatarMarkup(dog)}
        <div class="loop-dog-info">
          <div class="loop-dog-name">${window.LoopUtils.escapeHtml(
            dog.name,
          )}</div>
          <div class="loop-dog-meta">${meta}</div>
        </div>
      `;

      return card;
    },

    wallet({ balance = 0, expirationDateCount = null }) {
      const fmt = new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      });
      const expirationText =
        expirationDateCount === 0
          ? window.t("wallet.expiresInToday")
          : expirationDateCount < 0 || expirationDateCount === null
            ? window.t("wallet.expired")
            : expirationDateCount === 1
              ? window.t("wallet.expiresInOne")
              : window.t("wallet.expiresIn", expirationDateCount);

      const walletCard = window.LoopUtils.createElementWithClasses("div", [
        "wallet-wrapper",
        "justify-content-start",
      ]);
      walletCard.innerHTML = `
    <div class="wallet-header gap-1 d-flex align-items-center">
      ${window.LoopSVG.walletHeaderSvg()}
      <span class="wallet-header-text z-index-1">${window.t(
        "wallet.title",
      )}</span>
    </div>
    <div id="wallet-card-know-more" style="display: none;">
      <ul class="wallet-info-list">
        <li>
        ${window.t("wallet.earnCreditDesc")}
        </li>
        <li>
        ${window.t("wallet.expiryDesc")}
        </li>
      </ul>
    </div>
    <div id="wallet-card-amount">
    <div class="wallet-amount z-index-1">${fmt.format(balance)}</div>
    <div class="text-sm loop-text-muted z-index-1">${expirationText}</div>
    </div>
    ${
      window.loopProps?.subscription?.status === "cancelled"
        ? ``
        : `<button id="wallet-toggle-btn" class="btn-full-round-outline text-sm bold py-2 z-index-1">${window.t(
            "wallet.knowMore",
          )}</button>`
    }
    <img id="wallet-card-image" class="wallet-image" src="https://images.prismic.io/barkyn/aIu_hKTt2nPbZn4r_Wallet-photo.png?auto=format,compress" />
  `;

      const toggleButton = walletCard.querySelector("#wallet-toggle-btn");
      const knowMoreSection = walletCard.querySelector(
        "#wallet-card-know-more",
      );
      const amountSection = walletCard.querySelector("#wallet-card-amount");
      const walletImage = walletCard.querySelector("#wallet-card-image");

      toggleButton?.addEventListener("click", () => {
        const isKnowMoreVisible = knowMoreSection.style.display !== "none";

        if (isKnowMoreVisible) {
          // Currently showing "know more" content, switch to normal view
          knowMoreSection.style.display = "none";
          amountSection.style.display = "flex";
          walletImage.style.display = "block";
          toggleButton.textContent = window.t("wallet.knowMore");
        } else {
          // Currently showing normal view, switch to "know more" content
          knowMoreSection.style.display = "block";
          amountSection.style.display = "none";
          walletImage.style.display = "none";
          toggleButton.textContent = window.t("wallet.close");
        }
      });

      const walletCancelledCard = window.LoopUtils.createElementWithClasses(
        "div",
        [
          "loop-grid-item",
          "mx-n2",
          "mx-md-0",
          "d-flex",
          "wallet-cancelled-card",
        ],
      );
      walletCancelledCard.innerHTML = `<span>${window.t(
        "wallet.reactivateToUseWallet",
      )}</span>
      <span class="loop-text-muted text-sm">${window.t(
        "wallet.reactivateDesc",
      )}</span>`;

      return { walletCard, walletCancelledCard };
    },

    errorBanner: () => {
      const customerPortalBody = document.querySelector(
        ".loop-customer-portal-body",
      );

      if (!document.querySelector("loop-error-banner")) {
        const wrapper = document.createElement("div");
        wrapper.style = "display: flex; gap: 24px";
        wrapper.id = "loop-error-banner-wrapper";
        const newErrorBanner = document.createElement("loop-error-banner");
        wrapper.appendChild(newErrorBanner);
        customerPortalBody.insertAdjacentElement("afterbegin", wrapper);
      }
    },
  },

  // Button Components
  buttons: {
    moveShippingEditButton: () => {
      const titleElement = document.querySelector(
        ".loop-card-body .loop-h3.mb-2",
      );
      const editButton = document.querySelector(
        "#loop-shipping-address-edit-btn",
      );

      if (!titleElement || !editButton) return;

      const headerWrapper = window.LoopUI.helpers.createHeaderWrapper();
      headerWrapper.appendChild(titleElement.cloneNode(true));

      editButton.classList.remove("mt-2", "me-2");
      headerWrapper.appendChild(editButton);

      titleElement.replaceWith(headerWrapper);

      window.LoopUtils.addClasses("#loop-shipping-address-edit-btn", [
        "btn-full-round-outline",
        "loop-btn-outline",
        "text-sm",
        "bold",
        "py-2",
      ]);
      window.LoopUtils.removeClasses("#loop-shipping-address-edit-btn", [
        "loop-btn-size-sm",
        "loop-btn",
      ]);
    },

    movePaymentUpdateButton: () => {
      const titleElement = document.querySelector(
        "#loop-cp-subscription-detail-payment-method .flex-grow-1 .loop-card .loop-card-body div .loop-h3.mb-2",
      );
      const editButton = document.querySelector(
        "#loop-cp-payment-method-update-btn",
      );

      if (!titleElement || !editButton) return;

      const headerWrapper = window.LoopUI.helpers.createHeaderWrapper();
      headerWrapper.appendChild(titleElement.cloneNode(true));

      editButton.classList.remove("my-2", "me-2");
      headerWrapper.appendChild(editButton);

      titleElement.replaceWith(headerWrapper);

      window.LoopUtils.addClasses("#loop-cp-payment-method-update-btn", [
        "btn-full-round-outline",
        "loop-btn-outline",
        "text-sm",
        "bold",
        "py-2",
      ]);
      window.LoopUtils.removeClasses("#loop-cp-payment-method-update-btn", [
        "loop-btn-size-sm",
        "loop-btn",
      ]);

      window.LoopUtils.addClasses(
        "#loop-cp-subscription-detail-payment-method .flex-grow-1 .loop-card .loop-card-body",
        ["w-100"],
      );
    },

    moveUpsellSeeAllButton: () => {
      const titleElement = document.querySelector(".loop-card-upsell-header");
      const upsellButton = document.querySelector(
        "#loop-cp-upsell-view-more-btn",
      );

      if (!titleElement || !upsellButton) return;

      const headerWrapper = window.LoopUI.helpers.createHeaderWrapper();
      headerWrapper.appendChild(titleElement.cloneNode(true));
      headerWrapper.appendChild(upsellButton);

      titleElement.replaceWith(headerWrapper);

      window.LoopUtils.addClasses("#loop-cp-upsell-view-more-btn", [
        "btn-full-round-outline",
        "loop-btn-outline",
        "text-sm",
        "bold",
        "py-2",
      ]);
      window.LoopUtils.removeClasses("#loop-cp-upsell-view-more-btn", [
        "loop-btn-upsell",
      ]);
    },

    customizeUpsellDrawerSelectProductButton: () => {
      const upsellButtons = document.querySelectorAll(
        "#loop-upsell-drawer-body .mb-3 .me-2 .loop-card .loop-card-body .d-flex .d-flex.flex-column.justify-content-between .d-flex.justify-content-between .loop-btn.loop-btn-primary",
      );

      if (!upsellButtons || upsellButtons.length === 0) return;

      upsellButtons.forEach((button) => {
        button.textContent = "";
        button.innerHTML = window.LoopSVG.arrowRightSvg();
        button.classList.add("upsell-drawer-arrow-button");
      });
    },

    customQuantityEditor: ({
      initialQuantity,
      setQuantity,
      pricePerLine,
      walletBalance,
    }) => {
      const maxQuantity =
        initialQuantity + Math.max(1, +walletBalance / +pricePerLine);
      const quantityEditor = document.createElement("div");
      const quantityButtons = document.createElement("div");
      const quantityInputContainer = document.createElement("div");
      const quantityInput = document.createElement("input");

      const plus = document.createElement("button");
      const minus = document.createElement("button");
      const separator = document.createElement("div");

      plus.innerHTML = window.LoopSVG.plusSvg();
      minus.innerHTML = window.LoopSVG.minusSvg();

      separator.classList.add("quantity-separator");
      quantityEditor.classList.add("custom-quantity-editor");
      quantityButtons.classList.add("quantity-buttons");
      quantityInputContainer.classList.add("loop-form-input-container");

      quantityInputContainer.appendChild(quantityInput);
      quantityButtons.appendChild(minus);
      quantityButtons.appendChild(separator);
      quantityButtons.appendChild(plus);
      quantityEditor.appendChild(quantityInputContainer);
      quantityEditor.appendChild(quantityButtons);

      quantityInput.type = "number";
      quantityInput.value = initialQuantity;
      quantityInput.min = 1;

      quantityInput.addEventListener("input", (event) => {
        const value = Number(event.target.value);
        setQuantity(value);
      });

      plus.addEventListener("click", () => {
        const newQuantity = Number(quantityInput.value) + 1;

        if (newQuantity > maxQuantity) return;
        quantityInput.value = newQuantity;
        setQuantity(newQuantity);
      });

      minus.addEventListener("click", () => {
        const newQuantity = Number(quantityInput.value) - 1;

        if (newQuantity < 1) return;
        quantityInput.value = newQuantity;
        setQuantity(newQuantity);
      });

      return quantityEditor;
    },

    addLogoutButton: async (footerElement) => {
      const logoutButton = document.createElement("button");
      logoutButton.textContent = window.t("user.logout");
      logoutButton.classList.add(
        "loop-btn",
        "loop-btn-underline",
        "logout-footer-btn",
      );

      logoutButton.addEventListener("click", async () => {
        try {
          if (!window.clerkAuth) {
            console.error("Clerk not available for logout");
            return;
          }

          await window.clerkAuth.logout();
        } catch (error) {
          console.error("Logout failed:", error);
          alert("Logout failed. Please try again.");
        }
      });

      footerElement.classList.add("gap-3");
      footerElement.insertBefore(logoutButton, footerElement.firstChild);

      return logoutButton;
    },
  },

  // Drawer Components
  drawer: {
    storeCurrentData: () => {
      const loopDrawerTitle = document.getElementById(
        "loop-custom-drawer-title-text",
      );
      const loopDrawerContent = document.getElementById(
        "loop-custom-drawer-content",
      );

      drawerSteps.push({
        title: loopDrawerTitle ? loopDrawerTitle.textContent : "",
        content: loopDrawerContent ? loopDrawerContent.firstChild : "",
      });
    },
    stepBack: () => {
      if (drawerSteps.length === 0) return;
      const drawerStepBack = drawerSteps.pop();

      window.LoopUI.drawer.replaceDrawerInfo({
        title: drawerStepBack.title,
        content: drawerStepBack.content,
        shouldShowBack: drawerSteps.length > 0,
        shouldStoreState: false,
      });
    },
    createDrawer: ({ title, content, shouldShowBack = false }) => {
      // Reset drawer steps
      drawerSteps = [];
      const loopOverlayDiv = document.createElement("div");
      const loopDrawerDiv = document.createElement("div");
      const loopOverlay = document.createElement("div");
      const loopDrawer = document.createElement("div");
      const loopDrawerContent = document.createElement("div");
      const loopDrawerHeader = document.createElement("div");
      const loopDrawerBody = document.createElement("div");

      const headerContent = `
      <button class="loop-btn loop-btn-underline" id="loop-drawer-close-btn"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="loop-svg-current-color"><path d="M7.21926 6.89327L11.9045 2.20783C11.9756 2.13914 12.0323 2.05699 12.0714 1.96615C12.1104 1.87531 12.1309 1.77761 12.1318 1.67875C12.1326 1.57989 12.1138 1.48185 12.0764 1.39035C12.0389 1.29885 11.9836 1.21571 11.9137 1.14581C11.8438 1.0759 11.7607 1.02061 11.6692 0.983178C11.5777 0.945742 11.4797 0.926903 11.3808 0.927762C11.2819 0.928622 11.1842 0.949161 11.0934 0.988182C11.0026 1.0272 10.9204 1.08392 10.8517 1.15504L6.16628 5.84029L1.48103 1.15504C1.34089 1.01797 1.15235 0.941707 0.956331 0.942787C0.760311 0.943866 0.572625 1.02221 0.434003 1.1608C0.295382 1.2994 0.21701 1.48707 0.215896 1.68309C0.214782 1.87911 0.291015 2.06766 0.428052 2.20783L5.11349 6.89327L0.428052 11.5787C0.35765 11.6476 0.301607 11.7297 0.263167 11.8204C0.224727 11.911 0.204655 12.0084 0.204113 12.1069C0.203571 12.2054 0.222569 12.303 0.260008 12.3941C0.297446 12.4851 0.352581 12.5679 0.422221 12.6375C0.491861 12.7071 0.574621 12.7623 0.66571 12.7997C0.756799 12.8371 0.854406 12.8561 0.952881 12.8555C1.05136 12.855 1.14874 12.8349 1.2394 12.7964C1.33006 12.758 1.41219 12.7019 1.48103 12.6315L6.16628 7.94624L10.8517 12.6315C10.9971 12.7769 11.1877 12.8496 11.3781 12.8496C11.5685 12.8496 11.7593 12.7769 11.9045 12.6315C12.0441 12.4919 12.1225 12.3025 12.1225 12.1051C12.1225 11.9077 12.0441 11.7183 11.9045 11.5787L7.21926 6.89327Z"></path></svg></button>
      <div class="loop-h2" id="loop-custom-drawer-title">
        <div id="loop-custom-drawer-title-text">${title}</div>
      </div>
      `;

      loopOverlayDiv.classList.add("loop");
      loopDrawerDiv.classList.add("loop");
      loopOverlay.classList.add("loop-drawer-overlay");
      loopDrawer.classList.add("loop-drawer");
      loopDrawerContent.classList.add("loop-drawer-content");
      loopDrawerHeader.classList.add(
        "loop-drawer-header",
        "d-flex",
        "w-100",
        "p-3",
        "justify-content-between",
        "flex-row-reverse",
      );
      loopDrawerBody.classList.add("loop-drawer-body", "wallet-drawer");
      loopDrawerBody.setAttribute("id", "loop-custom-drawer-content");

      loopDrawerBody.appendChild(content);

      loopDrawerContent.appendChild(loopDrawerHeader);
      loopDrawerContent.appendChild(loopDrawerBody);
      loopDrawer.appendChild(loopDrawerContent);
      loopOverlayDiv.appendChild(loopOverlay);
      loopDrawerDiv.appendChild(loopDrawer);
      document.body.appendChild(loopOverlayDiv);
      document.body.appendChild(loopDrawerDiv);

      loopDrawerHeader.innerHTML = headerContent;

      // Add event listener to close button
      const closeButton = document.getElementById("loop-drawer-close-btn");
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          loopOverlay.classList.remove("enter-done");
          loopDrawer.classList.remove("enter-done");
          setTimeout(() => {
            loopOverlayDiv.remove();
            loopDrawerDiv.remove();
          }, 300);
        });
      }

      // Create back button (always present but initially hidden)
      const loopDrawerButton = document.getElementById(
        "loop-custom-drawer-title",
      );
      const backButton = document.createElement("div");
      backButton.setAttribute("id", "loop-custom-drawer-back-button");
      backButton.style.display = shouldShowBack ? "block" : "none";

      backButton.innerHTML = `
      <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.585999 8.41398L5.455 13.283C5.69 13.518 6.068 13.518 6.303 13.283L6.869 12.717C7.104 12.482 7.104 12.103 6.869 11.869L3 7.99998L15.4 7.99998C15.732 7.99998 16 7.73098 16 7.39998V6.59998C16 6.26798 15.732 5.99998 15.4 5.99998L3 5.99998L6.869 2.13098C7.104 1.89598 7.104 1.51798 6.869 1.28298L6.303 0.716978C6.068 0.481978 5.69 0.481978 5.455 0.716978L0.585999 5.58598C-0.195 6.36698 -0.195 7.63298 0.585999 8.41398Z" fill="black"/>
      </svg>
      `;

      loopDrawerButton.insertBefore(backButton, loopDrawerButton.firstChild);

      // Store current data if back button is shown
      if (shouldShowBack) {
        window.LoopUI.drawer.storeCurrentData();
      }

      // Add event listener to back button (only once)
      backButton.addEventListener("click", () => {
        window.LoopUI.drawer.stepBack();
      });

      loopDrawer.addEventListener("click", (e) => {
        if (!e.target.classList.contains("loop-drawer")) return;

        loopOverlay.classList.remove("enter-done");
        loopDrawer.classList.remove("enter-done");
        setTimeout(() => {
          loopOverlayDiv.remove();
          loopDrawerDiv.remove();
        }, 300);
      });

      setTimeout(() => {
        loopOverlay.classList.add("enter-done");
        loopDrawer.classList.add("appear-done");
        loopDrawer.classList.add("enter-done");
      }, 10);
    },

    replaceDrawerInfo: ({
      title,
      content,
      shouldShowBack,
      shouldStoreState = true,
    }) => {
      // Handle back button visibility
      const backButton = document.getElementById(
        "loop-custom-drawer-back-button",
      );
      if (backButton) {
        if (shouldShowBack) {
          // Store current data before showing back button (only if shouldStoreState is true)
          if (shouldStoreState) {
            window.LoopUI.drawer.storeCurrentData();
          }
          backButton.style.display = "block";
        } else {
          backButton.style.display = "none";
        }
      }

      if (title) {
        const titleContainer = document.getElementById(
          "loop-custom-drawer-title-text",
        );
        titleContainer.textContent = title;
      }

      if (content) {
        const contentContainer = document.getElementById(
          "loop-custom-drawer-content",
        );
        contentContainer.innerHTML = "";
        contentContainer.appendChild(content);
      }
    },

    customizeAddProductDrawer: async () => {
      // Global cache for allowed products
      if (!window.LoopWalletCache) {
        try {
          window.LoopWalletCache =
            await window.LoopService.Wallet.getAllowedProductsForWallet();
        } catch (e) {
          return; // Silently fail if API fails
        }
      }

      // Global cache for balance
      // Only fetch if undefined. If 0, it's a valid balance, so we don't fetch again.
      if (window.LoopWalletBalance === undefined) {
        try {
          const { balance } =
            await window.LoopService.Wallet.getBalanceAndExpiry(
              window.Shopify?.customerId,
            );
          window.LoopWalletBalance = balance;
        } catch (e) {
          return;
        }
      }

      const balance = window.LoopWalletBalance;
      const allDrawers = document.querySelectorAll(
        "#loop-cp-drawer-id-add-product",
      );

      if (allDrawers.length === 0) return;

      // --- Logic applied to each drawer instance ---
      const setupDrawer = (drawerElement) => {
        /**
         * Safely extracts price, quantity and subscription status from the DOM.
         * Returns null if essential elements are not yet rendered.
         */
        const calculateState = () => {
          try {
            // A. Attempt to find the price element
            // Note: Selectors might need adjustment if Loop changes HTML structure
            const priceEl = drawerElement.querySelector(
              ".loop-drawer-body .loop-text-p1",
            );
            if (!priceEl) return null;

            // B. Parse price string (e.g., "2,39 €" -> 2.39)
            const priceText = priceEl.innerText
              .replace(/[^\d,.]/g, "")
              .replace(",", ".");
            const unitPrice = parseFloat(priceText);
            if (isNaN(unitPrice)) return null;

            // C. Get current quantity
            const qtyInput = drawerElement.querySelector(
              "input[type='number'].loop-form-input",
            );
            let quantity = 1;
            if (qtyInput) {
              const val = parseInt(qtyInput.value);
              if (!isNaN(val)) quantity = val;
            }

            // D. Check if subscription mode is selected
            const subRadio = drawerElement.querySelector(
              "input[type='radio'][value='subscription']",
            );
            const isSubscription = subRadio && subRadio.checked;

            return {
              unitPrice,
              quantity,
              isSubscription,
              totalPrice: unitPrice * quantity,
            };
          } catch (e) {
            return null; // Fail gracefully during render phase
          }
        };

        /**
         * Formats a number as a locale price string (e.g. 1.95 → "1,95 €")
         */
        const formatPrice = (value) =>
          value.toFixed(2).replace(".", ",") + " €";

        /**
         * Updates the price display on both custom CTAs.
         *
         * Strategy:
         * 1. If subscription is selected, the drawer itself shows both prices
         *    inside .loop-product-drawer-price — read from there.
         * 2. Otherwise (one-off default), the drawer only shows the full price.
         *    The product card OUTSIDE the drawer (in the portal upsell list) always
         *    shows both prices. Match by the full-price value and read the discounted
         *    price from the card's non-strikethrough sibling.
         */
        const updateCustomCTAPrices = () => {
          const wrapper = drawerElement.querySelector(".custom-cta-wrapper");
          if (!wrapper) return;

          const parseLoopPrice = (el) => {
            if (!el) return 0;
            return (
              parseFloat(
                el.textContent.replace(/[^\d,]/g, "").replace(",", "."),
              ) || 0
            );
          };

          const qtyInput = drawerElement.querySelector(
            "input[type='number'].loop-form-input",
          );
          const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

          let fullUnitPrice = 0;
          let discountedUnitPrice = 0;

          const drawerStrike = drawerElement.querySelector(
            ".text-decoration-line-through",
          );
          if (drawerStrike) {
            fullUnitPrice = parseLoopPrice(drawerStrike);
            const drawerDiscounted = drawerElement.querySelector(
              ".loop-product-drawer-price .loop-text-p1",
            );
            discountedUnitPrice = parseLoopPrice(drawerDiscounted);
          } else {
            const drawerPriceEl = drawerElement.querySelector(
              ".loop-drawer-body .loop-text-p1",
            );
            fullUnitPrice = parseLoopPrice(drawerPriceEl);

            if (fullUnitPrice > 0) {
              const allRows = document.querySelectorAll(
                ".d-flex.align-items-baseline",
              );
              for (const row of allRows) {
                if (drawerElement.contains(row)) continue;
                const strike = row.querySelector(".text-decoration-line-through");
                if (!strike) continue;
                if (Math.abs(parseLoopPrice(strike) - fullUnitPrice) < 0.01) {
                  const discountedEl = Array.from(row.children).find(
                    (el) =>
                      !el.classList.contains("text-decoration-line-through"),
                  );
                  discountedUnitPrice = parseLoopPrice(discountedEl);
                  break;
                }
              }
            }
          }

          if (!fullUnitPrice) {
            const state = calculateState();
            if (!state) return;
            fullUnitPrice = state.unitPrice;
          }
          if (!discountedUnitPrice) discountedUnitPrice = fullUnitPrice;

          const fullTotal = fullUnitPrice * quantity;
          const discountedTotal = discountedUnitPrice * quantity;

          const oneoffPrice = wrapper.querySelector(
            '[data-cta-type="onetime"] .custom-cta-price',
          );
          if (oneoffPrice) oneoffPrice.textContent = formatPrice(fullTotal);

          const subPrice = wrapper.querySelector(
            '[data-cta-type="subscription"] .custom-cta-price',
          );
          if (subPrice) {
            if (discountedTotal < fullTotal) {
              subPrice.innerHTML =
                `<span style="text-decoration:line-through;opacity:0.5;font-size:0.85em;margin-right:4px">${formatPrice(fullTotal)}</span>` +
                `<span>${formatPrice(discountedTotal)}</span>`;
            } else {
              subPrice.textContent = formatPrice(fullTotal);
            }
          }
        };

        /**
         * Replaces Loop's single primary CTA with two explicit buttons:
         * subscription (primary style) on top, one-off (outline) below.
         */
        const reconcileCustomCTAs = () => {
          const buttonsContainer = drawerElement.querySelector(
            ".loop-drawer-footer > .d-flex.flex-column",
          );
          if (!buttonsContainer) return;
          if (buttonsContainer.querySelector(".custom-cta-wrapper")) return;

          const loopPrimaryBtn = buttonsContainer.querySelector(".loop-btn-primary");
          if (!loopPrimaryBtn) return;

          const subRadioEl = drawerElement.querySelector('input[value="subscription"]');
          const subChoiceEl = subRadioEl?.parentElement;
          const oneoffRadioEl = drawerElement.querySelector('input[value="onetime"]');
          const oneoffChoiceEl = oneoffRadioEl?.parentElement;

          const subLabelText = subChoiceEl ? subChoiceEl.textContent.trim() : "Aggiungi al piano";
          const oneoffText = oneoffChoiceEl ? oneoffChoiceEl.textContent.trim() : "Aggiungi a tutti i tuoi ordini";
          const subText = subLabelText.split(",")[0].trim();

          const discountMatch = subLabelText.match(/(\d+)%/);
          const discountPct = discountMatch ? parseFloat(discountMatch[1]) : 0;

          const buildBtn = (text, isPrimary, ctaType) => {
            const btn = document.createElement("button");
            btn.className =
              "loop-btn loop-btn-size-lg loop-drawer-btn w-100 custom-cta-btn";
            btn.dataset.ctaType = ctaType;
            if (isPrimary) {
              btn.classList.add("loop-btn-primary");
            } else {
              btn.style.cssText = "background:white;color:black;border:1px solid black;";
            }

            const textSpan = document.createElement("span");
            textSpan.style.textAlign = "left";
            textSpan.textContent = text;

            const priceSpan = document.createElement("span");
            priceSpan.className = "custom-cta-price";
            priceSpan.style.cssText =
              "display:flex;align-items:baseline;flex-shrink:0;margin-left:12px;";

            btn.appendChild(textSpan);
            btn.appendChild(priceSpan);
            return btn;
          };

          const subBtn = buildBtn(subText, true, "subscription");
          subBtn.addEventListener("click", () => {
            const radio = drawerElement.querySelector('input[value="subscription"]');
            if (radio) {
              radio.click();
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
            setTimeout(() => loopPrimaryBtn.click(), 50);
          });

          const oneoffBtn = buildBtn(oneoffText, false, "onetime");
          oneoffBtn.addEventListener("click", () => {
            const radio = drawerElement.querySelector('input[value="onetime"]');
            if (radio) {
              radio.click();
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
            setTimeout(() => loopPrimaryBtn.click(), 50);
          });

          const wrapper = document.createElement("div");
          wrapper.className = "custom-cta-wrapper w-100 d-flex flex-column";
          wrapper.style.gap = "8px";
          wrapper.dataset.discountPct = discountPct;
          wrapper.appendChild(subBtn);
          wrapper.appendChild(oneoffBtn);

          loopPrimaryBtn.style.display = "none";
          loopPrimaryBtn.after(wrapper);

          let watchAttempts = 0;
          const watchPoll = setInterval(() => {
            watchAttempts++;
            if (watchAttempts > 50) { clearInterval(watchPoll); return; }

            const stillThere = drawerElement.querySelector(".custom-cta-wrapper");
            if (!stillThere) {
              reconcileCustomCTAs();
              return;
            }

            updateCustomCTAPrices();
            const priceSpan = wrapper.querySelector(
              '[data-cta-type="onetime"] .custom-cta-price',
            );
            if (priceSpan?.textContent?.trim() && watchAttempts >= 30) clearInterval(watchPoll);
          }, 100);
        };

        /**
         * Updates the Wallet Button visibility, state (disabled/enabled),
         * and the information tag based on the calculated state.
         */
        const updateUI = () => {
          updateCustomCTAPrices();

          const wrapper = drawerElement.querySelector(".wallet-button-wrapper");
          if (!wrapper) return;

          const state = calculateState();

          // If state is null, it means Loop is still loading.
          // We hide the wrapper temporarily to avoid showing broken UI.
          if (!state) {
            wrapper.style.display = "none";
            return;
          }

          const { isSubscription, totalPrice } = state;
          const btn = wrapper.querySelector("button");
          const tag = wrapper.querySelector(".wallet-tag");

          // Update wallet button price (one-off total)
          const walletPriceSpan = wrapper.querySelector(".wallet-cta-price");
          if (walletPriceSpan) walletPriceSpan.textContent = formatPrice(totalPrice);

          // Hide wallet option for subscriptions
          if (isSubscription) {
            wrapper.style.display = "none";
            return;
          }

          // Show wrapper and update logic
          wrapper.style.display = "flex";
          tag.classList.remove(
            "wallet-tag-sufficient",
            "wallet-tag-insufficient",
          );

          if (balance < totalPrice) {
            // Insufficient Balance
            tag.textContent = window.t(
              "wallet.notEnoughCredit",
              balance.toFixed(2),
            );
            tag.classList.add("wallet-tag-insufficient");

            btn.disabled = true;
            btn.classList.add("disabled"); // Ensure visual disabled state
            btn.style.opacity = "0.5";
            btn.style.cursor = "not-allowed";
          } else {
            // Sufficient Balance
            tag.textContent = window.t(
              "wallet.youHaveCredit",
              balance.toFixed(2),
            );
            tag.classList.add("wallet-tag-sufficient");

            btn.disabled = false;
            btn.classList.remove("disabled");
            btn.style.opacity = "1";
            btn.style.cursor = "pointer";
          }
        };

        /**
         * Injects the Wallet Button into the drawer footer if allowed.
         */
        const reconcileWalletButton = () => {
          // 1. Validation checks
          const content = drawerElement.querySelector(".loop-drawer-content");
          if (!content) return;

          const pId = parseInt(String(content.dataset.productId || "").trim());
          // If product is not in the allowed list, stop here.
          if (!window.LoopWalletCache.includes(pId)) return;

          const buttonsContainer = drawerElement.querySelector(
            ".loop-drawer-footer > .d-flex.flex-column",
          );
          if (!buttonsContainer) return;

          // 2. Check if button exists
          if (buttonsContainer.querySelector(".wallet-button-wrapper")) {
            updateUI(); // Just update state if already exists
            return;
          }

          // 3. Create DOM Elements
          const buttonWrapper = document.createElement("div");
          buttonWrapper.className =
            "wallet-button-wrapper mt-2 w-100 d-flex flex-column align-items-center";

          const walletLabel = window.t
            ? window.t("wallet.addProductWithWallet")
            : "Usa credito wallet";
          const walletIcon = window.LoopSVG ? window.LoopSVG.walletDrawerSvg() : "";

          buttonWrapper.innerHTML = `
        <button class="loop-btn loop-btn-size-lg loop-drawer-btn w-100 upsell-drawer-outline-btn"
          style="background:white;color:black;border:1px solid black;">
          <span style="display:flex;align-items:center;gap:8px;text-align:left;">
            ${walletIcon}${walletLabel}
          </span>
          <span class="wallet-cta-price" style="flex-shrink:0;margin-left:12px;"></span>
        </button>
        <span class="wallet-tag text-sm"></span>
      `;

          // 4. Add Click Handler
          const btn = buttonWrapper.querySelector("button");
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            window.LoopUI.helpers.withButtonSpinner(btn, async () => {
              await window.LoopFeatures.wallet.addProductWithWalletCredit();
              window.location.reload();
            });
          });

          // 5. Append to Footer
          buttonsContainer.appendChild(buttonWrapper);

          // 6. FIX: Force immediate updates to ensure tag appears
          // We poll briefly to catch the moment the price is rendered
          let attempts = 0;
          const initialPoll = setInterval(() => {
            updateUI();
            attempts++;
            // Stop after 1 second (20 attempts * 50ms)
            if (attempts > 20) clearInterval(initialPoll);
          }, 50);
        };

        // --- Setup Event Listeners & Observers ---
        if (!drawerElement.dataset.walletObserverAttached) {
          // Event Delegation: Safely listen to events bubbling up from the drawer
          // Using timeouts to allow Loop's React/VirtualDOM to update state first
          drawerElement.addEventListener("change", () =>
            setTimeout(updateUI, 50),
          );
          drawerElement.addEventListener("input", () =>
            setTimeout(updateUI, 50),
          );

          // Catch clicks on quantity buttons (+/-)
          drawerElement.addEventListener("click", (e) => {
            if (e.target.closest("button")) {
              setTimeout(updateUI, 100);
            }
          });

          // MutationObserver: Re-inject buttons if Loop re-renders the footer
          const observer = new MutationObserver((mutations) => {
            let shouldReconcile = false;
            for (const mutation of mutations) {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "data-product-id"
              ) {
                shouldReconcile = true;
              }

              const hasClass = (node, cls) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.classList?.contains(cls) ||
                  !!node.querySelector?.("." + cls));

              if (mutation.type === "childList") {
                mutation.addedNodes.forEach((node) => {
                  if (
                    hasClass(node, "loop-btn-primary") &&
                    !hasClass(node, "custom-cta-btn")
                  ) {
                    shouldReconcile = true;
                  }
                });
                mutation.removedNodes.forEach((node) => {
                  if (
                    hasClass(node, "custom-cta-wrapper") ||
                    hasClass(node, "wallet-button-wrapper")
                  ) {
                    shouldReconcile = true;
                  }
                });
              }
            }
            if (shouldReconcile) {
              [0, 200, 500, 1000, 1800, 3000].forEach((d) => {
                setTimeout(reconcileCustomCTAs, d);
                setTimeout(reconcileWalletButton, d);
              });
            }
          });

          observer.observe(drawerElement, {
            childList: true,
            subtree: true,
            attributes: true,
          });

          drawerElement.dataset.walletObserverAttached = "true";

          // Initial Run
          reconcileCustomCTAs();
          reconcileWalletButton();

          setInterval(() => {
            reconcileCustomCTAs();
            reconcileWalletButton();
            updateCustomCTAPrices();
          }, 800);
        }

        [150, 300, 500, 800, 1200, 2000, 3000].forEach((d) => setTimeout(reconcileCustomCTAs, d));
      };

      allDrawers.forEach(setupDrawer);
    },

    createDrawerCopyForWalletLine: async (line, shouldShowBack = false) => {
      let quantity = line.quantity;

      const { balance } = await window.LoopService.Wallet.getBalanceAndExpiry(
        window.Shopify.customerId,
      );
      const quantityEditor = window.LoopUI.buttons.customQuantityEditor({
        initialQuantity: quantity,
        setQuantity: (newQuantity) => {
          quantity = newQuantity;
        },
        pricePerLine: line.basePrice,
        walletBalance: balance,
      });
      const loopOverlayDiv = document.createElement("div");
      const loopDrawerDiv = document.createElement("div");
      const loopOverlay = document.createElement("div");
      const loopDrawer = document.createElement("div");
      const loopDrawerContent = document.createElement("div");
      const loopDrawerHeader = document.createElement("div");
      const loopDrawerBody = document.createElement("div");
      const loopDrawerButtonContainer = document.createElement("div");
      const removeButton = document.createElement("button");
      const updateButton = document.createElement("button");
      const lineImg = document.createElement("img");
      const productTitle = document.createElement("div");

      lineImg.src = line.variantImage;
      productTitle.textContent = line.title;
      productTitle.classList.add("loop-h2");
      const headerContent = `
      <button id="loop-drawer-close-btn" class="loop-btn loop-btn-underline"><svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="loop-svg-current-color"><path d="M7.21926 6.89327L11.9045 2.20783C11.9756 2.13914 12.0323 2.05699 12.0714 1.96615C12.1104 1.87531 12.1309 1.77761 12.1318 1.67875C12.1326 1.57989 12.1138 1.48185 12.0764 1.39035C12.0389 1.29885 11.9836 1.21571 11.9137 1.14581C11.8438 1.0759 11.7607 1.02061 11.6692 0.983178C11.5777 0.945742 11.4797 0.926903 11.3808 0.927762C11.2819 0.928622 11.1842 0.949161 11.0934 0.988182C11.0026 1.0272 10.9204 1.08392 10.8517 1.15504L6.16628 5.84029L1.48103 1.15504C1.34089 1.01797 1.15235 0.941707 0.956331 0.942787C0.760311 0.943866 0.572625 1.02221 0.434003 1.1608C0.295382 1.2994 0.21701 1.48707 0.215896 1.68309C0.214782 1.87911 0.291015 2.06766 0.428052 2.20783L5.11349 6.89327L0.428052 11.5787C0.35765 11.6476 0.301607 11.7297 0.263167 11.8204C0.224727 11.911 0.204655 12.0084 0.204113 12.1069C0.203571 12.2054 0.222569 12.303 0.260008 12.3941C0.297446 12.4851 0.352581 12.5679 0.422221 12.6375C0.491861 12.7071 0.574621 12.7623 0.66571 12.7997C0.756799 12.8371 0.854406 12.8561 0.952881 12.8555C1.05136 12.855 1.14874 12.8349 1.2394 12.7964C1.33006 12.758 1.41219 12.7019 1.48103 12.6315L6.16628 7.94624L10.8517 12.6315C10.9971 12.7769 11.1877 12.8496 11.3781 12.8496C11.5685 12.8496 11.7593 12.7769 11.9045 12.6315C12.0441 12.4919 12.1225 12.3025 12.1225 12.1051C12.1225 11.9077 12.0441 11.7183 11.9045 11.5787L7.21926 6.89327Z"></path></svg></button>
      <div id="loop-custom-drawer-title" class="loop-h2">
        <div id="loop-custom-drawer-title-text">${window.t(
          "user.editProduct",
        )}</div>
      </div>
      `;

      removeButton.classList.add(
        "loop-btn",
        "loop-btn-danger",
        "loop-btn-size-lg",
      );
      removeButton.innerHTML = window.t("user.removeProduct");
      removeButton.addEventListener("click", async () => {
        await window.LoopService.Onetime.remove({
          subscriptionId: window.loopProps.subscription.id.toString(),
          lineId: line.id,
        });

        window.location.reload();
      });

      updateButton.classList.add(
        "loop-btn",
        "loop-btn-primary",
        "loop-btn-size-lg",
      );
      updateButton.innerHTML = window.t("ui.update");
      updateButton.addEventListener("click", async () => {
        await window.LoopService.Onetime.updateQuantity({
          subscriptionId: window.loopProps.subscription.id.toString(),
          lineId: line.id,
          quantity,
          externalVariantId: line.variantId.toString(),
        });

        window.location.reload();
      });

      loopOverlayDiv.classList.add("loop");
      loopDrawerDiv.classList.add("loop");
      loopOverlay.classList.add("loop-drawer-overlay");
      loopDrawer.classList.add("loop-drawer");
      loopDrawerContent.classList.add("loop-drawer-content");
      loopDrawerHeader.classList.add(
        "loop-drawer-header",
        "d-flex",
        "w-100",
        "p-3",
        "justify-content-between",
        "flex-row-reverse",
      );
      loopDrawerBody.classList.add("loop-drawer-body", "wallet-drawer");
      loopDrawerBody.setAttribute("id", "loop-custom-drawer-content");
      loopDrawerButtonContainer.classList.add("loop-wallet-button-container");

      loopDrawerBody.appendChild(lineImg);
      loopDrawerBody.appendChild(productTitle);
      loopDrawerBody.appendChild(quantityEditor);
      loopDrawerButtonContainer.appendChild(updateButton);
      loopDrawerButtonContainer.appendChild(removeButton);

      loopDrawerContent.appendChild(loopDrawerHeader);
      loopDrawerContent.appendChild(loopDrawerBody);
      loopDrawerContent.appendChild(loopDrawerButtonContainer);
      loopDrawer.appendChild(loopDrawerContent);
      loopOverlayDiv.appendChild(loopOverlay);
      loopDrawerDiv.appendChild(loopDrawer);
      document.body.appendChild(loopOverlayDiv);
      document.body.appendChild(loopDrawerDiv);

      loopDrawerHeader.innerHTML = headerContent;

      // Add event listener to close button
      const closeButton = document.getElementById("loop-drawer-close-btn");
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          loopOverlay.classList.remove("enter-done");
          loopDrawer.classList.remove("enter-done");
          setTimeout(() => {
            loopOverlayDiv.remove();
            loopDrawerDiv.remove();
          }, 300);
        });
      }

      // Create back button (always present but initially hidden)
      const loopDrawerButton = document.getElementById(
        "loop-custom-drawer-title",
      );
      if (loopDrawerButton) {
        const backButton = document.createElement("div");
        backButton.setAttribute("id", "loop-custom-drawer-back-button");
        backButton.style.display = shouldShowBack ? "block" : "none";

        backButton.innerHTML = `
        <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M0.585999 8.41398L5.455 13.283C5.69 13.518 6.068 13.518 6.303 13.283L6.869 12.717C7.104 12.482 7.104 12.103 6.869 11.869L3 7.99998L15.4 7.99998C15.732 7.99998 16 7.73098 16 7.39998V6.59998C16 6.26798 15.732 5.99998 15.4 5.99998L3 5.99998L6.869 2.13098C7.104 1.89598 7.104 1.51798 6.869 1.28298L6.303 0.716978C6.068 0.481978 5.69 0.481978 5.455 0.716978L0.585999 5.58598C-0.195 6.36698 -0.195 7.63298 0.585999 8.41398Z" fill="black"/>
        </svg>
        `;

        loopDrawerButton.insertBefore(backButton, loopDrawerButton.firstChild);

        // Store current data if back button is shown
        if (shouldShowBack) {
          window.LoopUI.drawer.storeCurrentData();
        }

        // Add event listener to back button (only once)
        backButton.addEventListener("click", () => {
          window.LoopUI.drawer.stepBack();
        });
      }

      loopDrawer.addEventListener("click", (e) => {
        if (!e.target.classList.contains("loop-drawer")) return;

        loopOverlay.classList.remove("enter-done");
        loopDrawer.classList.remove("enter-done");
        setTimeout(() => {
          loopOverlayDiv.remove();
          loopDrawerDiv.remove();
        }, 300);
      });

      setTimeout(() => {
        loopOverlay.classList.add("enter-done");
        loopDrawer.classList.add("appear-done");
        loopDrawer.classList.add("enter-done");
      }, 10);

      if (line) {
        loopDrawerContent.dataset.productId = line.productId;
        loopDrawerContent.dataset.variantId = line.variantId;
      }
    },
  },

  // Content Enhancements
  enhancements: {
    addLastOrderInfo: (cardSelector, dateString = "XX-XX-XXXX") => {
      const cardBody = document.querySelector(cardSelector);
      if (!cardBody) return;

      const lastOrderElement = window.LoopUtils.createElementWithClasses(
        "div",
        ["text-sm", "loop-text-muted", "mt-4"],
      );
      lastOrderElement.textContent = `Last order was charged on ${dateString}`;
      cardBody.appendChild(lastOrderElement);
    },

    addPriceInfo: (cardSelector, priceText) => {
      const flexContainer = document.querySelector(cardSelector);
      if (!flexContainer) return;

      // Check if price info already exists
      if (flexContainer.querySelector(".price-info")) {
        return;
      }

      flexContainer.classList.add("mobile-column-flex");

      const priceInfoElement = window.LoopUtils.createElementWithClasses(
        "div",
        ["flex", "gap-2", "price-info"],
      );

      const priceTextElement = window.LoopUtils.createElementWithClasses(
        "div",
        ["price-text", "bolder"],
      );
      priceTextElement.textContent = priceText;

      const deliveryPriceTextElement =
        window.LoopUtils.createElementWithClasses("div", [
          "text-sm",
          "loop-text-muted",
        ]);
      deliveryPriceTextElement.textContent = window.loopProps?.subscription
        ?.deliveryPrice
        ? `+ ${window.t(
            "shipping.shippingCost",
            window.loopProps.subscription.deliveryPrice
              .toFixed(2)
              .replace(".", ","),
          )}`
        : window.t("shipping.freeShipping");

      priceInfoElement.append(priceTextElement, deliveryPriceTextElement);
      flexContainer.appendChild(priceInfoElement);
    },
  },

  // Helper Functions
  helpers: {
    createHeaderWrapper: () => {
      return window.LoopUtils.createElementWithClasses("div", [
        "d-flex",
        "justify-content-between",
        "align-items-start",
        "mb-2",
      ]);
    },

    getSelectedProductPrice: () => {
      try {
        const priceElement = document.querySelector(
          "#loop-cp-drawer-id-add-product .loop-drawer-body .mx-2 .d-flex.flex-column.my-2 .d-flex.align-items-baseline .loop-text-p1",
        );

        if (priceElement) {
          // Extract price from text (assumes format like "25,99€")
          const priceText = priceElement.textContent.replace(/[^\d.,]/g, "");
          return parseFloat(priceText.replace(",", ".")) || 0;
        }

        return 0;
      } catch (error) {
        console.error("Error getting product price:", error);
        return 0;
      }
    },

    isSubscriptionSelected: () => {
      try {
        const subscriptionInput = document.querySelector(
          "#loop-cp-drawer-id-add-product input[type='radio'][value='subscription']:checked",
        );

        return !!subscriptionInput;
      } catch (error) {
        console.error("Error checking subscription status:", error);
        return false;
      }
    },
  },
  drawerContent: {
    yourDogs: async () => {
      const dogs = await window.LoopFeatures.dogs.getAllDogs();

      const content = document.createElement("div");
      content.classList.add("loop-dogs-body");

      if (dogs.length === 0) {
        const empty = window.LoopUtils.createElementWithClasses("p", [
          "loop-dogs-empty",
        ]);
        empty.textContent = window.t("dogs.noDogs");
        content.appendChild(empty);
      }

      dogs.forEach((dog) => {
        const dogCard = window.LoopUI.cards.dogCard(dog);
        content.appendChild(dogCard);
      });

      const addNewDogButton = window.LoopUI.cards.addNewDogButton();

      content.appendChild(addNewDogButton);

      return { title: window.t("dogs.yourDogs"), content };
    },
    newDog: async () => {
      let name = "";
      let gender = "male";
      let lifeStage = "puppy";
      let weight = 1;

      const formError = window.LoopUI.cards.dogFormError();

      const onSubmitAction = async (e) => {
        try {
          e.preventDefault();
          formError.clear();
          const formElement = e.target.closest("form");
          const formData = new FormData(formElement);

          await window.BarkynDogsApi.create(
            window.LoopUtils.buildDogApiPayload({
              name: formData.get("name"),
              gender: formData.get("gender"),
              life_stage: formData.get("lifeStage"),
              weight: formData.get("weight"),
              country: window.LoopUtils.getStoreCountry(),
            }),
          );

          window.LoopFeatures?.dogs?._invalidateCache?.();
          window.location.reload();
        } catch (error) {
          console.error("Error creating dog:", error);
          formError.show(error);
        }
      };
      const nameInput = window.LoopUI.formObjects.formElementTypes.TEXT({
        placeholder: window.t("dogs.enterDogName"),
        title: window.t("dogs.name"),
        value: name,
        onValueChange: (value) => {
          name = value;
        },
        name: "name",
      });

      const genderInput = window.LoopUI.formObjects.formElementTypes.SELECT({
        title: window.t("dogs.gender"),
        value: gender,
        options: [
          { label: window.t("dogs.male"), value: "male" },
          { label: window.t("dogs.female"), value: "female" },
        ],
        onValueChange: (value) => {
          gender = value;
        },
        name: "gender",
      });

      const lifeStageInput = window.LoopUI.formObjects.formElementTypes.SELECT({
        title: window.t("dogs.lifeStage"),
        value: lifeStage,
        options: [
          { label: window.t("dogs.puppy"), value: "puppy" },
          { label: window.t("dogs.adult"), value: "adult" },
          { label: window.t("dogs.senior"), value: "senior" },
        ],
        onValueChange: (value) => {
          lifeStage = value;
        },
        name: "lifeStage",
      });

      const weightInput = window.LoopUI.formObjects.formElementTypes.TEXT({
        placeholder: window.t("dogs.enterDogWeight"),
        title: window.t("dogs.weight"),
        value: weight,
        unit: "kg",
        type: "number",
        inputMode: "numeric",
        min: 1,
        onValueChange: (value) => {
          weight = value;
        },
        name: "weight",
      });

      const form = window.LoopUI.formObjects.form({
        elements: [nameInput, genderInput, lifeStageInput, weightInput],
        buttons: [
          {
            type: "button",
            label: window.t("ui.save"),
            classes: ["loop-form-button", "loop-form-submit-button"],
            onClick: (e) => {
              e.preventDefault();
              const button = e.currentTarget;
              window.LoopUI.helpers.withButtonSpinner(button, () =>
                onSubmitAction(e),
              );
            },
          },
        ],
      });
      form.classList.add("loop-dog-form");

      const content = window.LoopUtils.createElementWithClasses("div", [
        "loop-dog-body",
      ]);
      content.appendChild(form);
      content.appendChild(formError.element);

      return { title: window.t("dogs.addNewDog"), content };
    },
    editDog: async (dog) => {
      const content = document.createElement("div");
      content.classList.add("loop-dog-body");

      const formError = window.LoopUI.cards.dogFormError();

      const onSubmitAction = async (event) => {
        event.preventDefault();
        formError.clear();
        const formData = new FormData(event.target.closest("form"));

        // PATCH wants the full editable state, so these changes are merged
        // onto the dog by BarkynDogsApi.save — fields the form doesn't show
        // (image_url, scoop_level, breed, …) are carried over rather than lost.
        const dogData = window.LoopUtils.buildDogApiPayload({
          name: formData.get("name"),
          gender: formData.get("gender"),
          life_stage: formData.get("lifeStage"),
          weight: formData.get("weight"),
        });

        // Checked here, not just in the API client: `save` merges onto the
        // existing dog, so an emptied name or weight would silently fall back
        // to the stored value instead of telling the user.
        const missingFields = window.BarkynDogsApi.findMissingRequired(dogData);
        if (missingFields.length) {
          formError.show({ missingFields });
          return;
        }

        try {
          if (dog.id) {
            await window.BarkynDogsApi.save(dog, dogData);
          } else {
            await window.BarkynDogsApi.create({
              ...dogData,
              country: window.LoopUtils.getStoreCountry(),
            });
          }

          window.LoopFeatures?.dogs?._invalidateCache?.();
          window.location.reload();
        } catch (error) {
          console.error("Error saving dog:", error);
          formError.show(error);
        }
      };
      const onDeleteProfile = async () => {
        const drawerContent =
          await window.LoopUI.drawerContent.deleteDogConfirmation(dog);
        window.LoopUI.drawer.replaceDrawerInfo({
          title: drawerContent.title,
          content: drawerContent.content,
          shouldShowBack: true,
          shouldStoreState: true,
        });
      };

      // Photo is display-only for now — no picker until there's a bucket to
      // upload to.
      content.innerHTML = window.LoopUI.cards.dogAvatarMarkup(dog, {
        sizeClass: "loop-dog-image-big",
      });

      const nameInput = window.LoopUI.formObjects.formElementTypes.TEXT({
        placeholder: window.t("dogs.enterDogName"),
        title: window.t("dogs.name"),
        value: dog.name,
        onValueChange: (value) => {
          dog.name = value;
        },
        name: "name",
      });

      const genderInput = window.LoopUI.formObjects.formElementTypes.SELECT({
        title: window.t("dogs.gender"),
        value: dog.gender,
        options: [
          { label: window.t("dogs.male"), value: "male" },
          { label: window.t("dogs.female"), value: "female" },
        ],
        onValueChange: (value) => {
          dog.gender = value;
        },
        name: "gender",
      });

      const lifeStageInput = window.LoopUI.formObjects.formElementTypes.SELECT({
        title: window.t("dogs.lifeStage"),
        value: dog.life_stage,
        options: [
          { label: window.t("dogs.puppy"), value: "puppy" },
          { label: window.t("dogs.adult"), value: "adult" },
          { label: window.t("dogs.senior"), value: "senior" },
        ],
        onValueChange: (value) => {
          dog.life_stage = value;
        },
        name: "lifeStage",
      });

      const weightInput = window.LoopUI.formObjects.formElementTypes.TEXT({
        placeholder: window.t("dogs.enterDogWeight"),
        title: window.t("dogs.weight"),
        value: dog.weight,
        unit: "kg",
        type: "number",
        inputMode: "numeric",
        min: 1,
        onValueChange: (value) => {
          dog.weight = value;
        },
        name: "weight",
      });
      const form = window.LoopUI.formObjects.form({
        elements: [nameInput, genderInput, lifeStageInput, weightInput],
        buttons: [
          {
            type: "submit",
            label: window.t("ui.save"),
            classes: ["loop-form-button", "loop-form-submit-button"],
            onClick: (e) => {
              e.preventDefault();
              const button = e.currentTarget;
              window.LoopUI.helpers.withButtonSpinner(button, () =>
                onSubmitAction(e),
              );
            },
          },
          {
            type: "button",
            label: window.t("dogs.deleteProfile"),
            classes: ["loop-form-button", "loop-form-delete-button"],
            onClick: (e) => {
              e.preventDefault();
              onDeleteProfile();
            },
          },
        ],
      });
      form.classList.add("loop-dog-form");

      content.appendChild(form);
      content.appendChild(formError.element);

      return { title: dog.name, content };
    },
    deleteDogConfirmation: async (dog) => {
      const content = document.createElement("div");
      content.classList.add("loop-content-drawer");

      content.innerHTML = `
      <div>
        <p class="loop-drawer-title">${window.t(
          "dogs.deleteDogConfirmation",
        )}</p>
        <p>${window.t("dogs.deleteDogWarning")}</p>
        <p>${window.t("dogs.deleteDogNote")}</p>
      </div>
      `;

      const buttonsContainer = document.createElement("div");
      buttonsContainer.classList.add("loop-form-buttons");
      const deleteButton = document.createElement("button");

      deleteButton.textContent = window.t("dogs.deleteProfile");
      deleteButton.classList.add("loop-form-button", "loop-form-submit-button");
      deleteButton.addEventListener("click", async (event) => {
        await window.LoopUI.helpers.withButtonSpinner(
          event.currentTarget,
          async () => {
            try {
              await window.BarkynDogsApi.remove(dog.id);
              window.LoopFeatures?.dogs?._invalidateCache?.();
              window.location.reload();
            } catch (error) {
              console.error("Error deleting dog:", error);
            }
          },
        );
      });

      const cancelButton = document.createElement("button");
      cancelButton.textContent = window.t("ui.cancel");
      cancelButton.classList.add("loop-form-button", "loop-form-cancel-button");

      cancelButton.addEventListener("click", () => {
        window.LoopUI.drawer.stepBack();
      });

      buttonsContainer.appendChild(deleteButton);
      buttonsContainer.appendChild(cancelButton);
      content.appendChild(buttonsContainer);
      return { title: dog.name, content };
    },
  },

  formObjects: {
    formElementTypes: {
      FORM: (data) => window.LoopUI.formObjects.form(data),
      TEXT: (data) => window.LoopUI.formObjects.textInput(data),
      SELECT: (data) => window.LoopUI.formObjects.selectInput(data),
      QUANTITY: (data) => window.LoopUI.formObjects.quantityInput(data),
      CHECKBOX: (data) => window.LoopUI.formObjects.checkboxInput(data),
    },
    form: ({ elements, buttons }) => {
      const form = document.createElement("form");
      const fieldset = document.createElement("fieldset");
      fieldset.classList.add("loop-form-fieldset");
      form.classList.add("loop-form");
      elements.forEach((element) => {
        fieldset.appendChild(element);
      });

      form.appendChild(fieldset);

      if (buttons) {
        const buttonsFieldset = document.createElement("fieldset");
        buttonsFieldset.classList.add("loop-form-buttons");

        buttons.forEach((btn) => {
          const button = document.createElement("button");
          button.type = btn.type;
          button.textContent = btn.label;
          button.classList.add(...btn.classes);
          button.addEventListener("click", btn.onClick);
          buttonsFieldset.appendChild(button);
        });

        form.appendChild(buttonsFieldset);
      }

      return form;
    },
    textInput: ({
      placeholder,
      title,
      value,
      onValueChange,
      name,
      unit = "",
      type = "text",
      inputMode,
      min,
    }) => {
      const inputContainer = document.createElement("div");
      const input = document.createElement("input");
      input.name = name;
      const label = document.createElement("span");

      label.textContent = title;
      input.type = type;
      input.placeholder = placeholder;
      input.value = value;
      if (inputMode) input.inputMode = inputMode;
      if (min !== undefined) input.min = min;

      inputContainer.classList.add("loop-form-input-container");
      label.classList.add("loop-form-input-label");
      input.classList.add("loop-form-input");

      inputContainer.appendChild(label);

      if (unit) {
        // Unit sits inside the field, right after the typed value (e.g. "12 kg")
        const inputWrapper = window.LoopUtils.createElementWithClasses("div", [
          "loop-form-input-wrapper",
        ]);
        const unitSpan = window.LoopUtils.createElementWithClasses("span", [
          "loop-form-input-unit",
        ]);
        unitSpan.textContent = unit;

        inputWrapper.appendChild(input);
        inputWrapper.appendChild(unitSpan);
        inputContainer.appendChild(inputWrapper);
      } else {
        inputContainer.appendChild(input);
      }

      input.addEventListener("input", (event) => {
        onValueChange(event.target.value);
      });

      inputContainer.addEventListener("click", () => {
        input.focus();
      });

      return inputContainer;
    },
    selectInput: ({ title, value, options, onValueChange, name }) => {
      const selectContainer = document.createElement("div");
      const select = document.createElement("select");
      select.name = name;
      const label = document.createElement("span");
      const selectWrapper = document.createElement("div");
      const arrowIcon = document.createElement("span");

      label.textContent = title;
      select.value = value;

      // SVG arrow down
      arrowIcon.innerHTML = `
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      selectContainer.classList.add("loop-form-input-container");
      label.classList.add("loop-form-input-label");
      select.classList.add("loop-form-input");
      selectWrapper.classList.add("loop-select-wrapper");
      arrowIcon.classList.add("loop-select-arrow");

      // Crear las opciones del select
      options.forEach((option) => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.label;
        if (option.value === value) {
          optionElement.selected = true;
        }
        select.appendChild(optionElement);
      });

      selectWrapper.appendChild(select);
      selectWrapper.appendChild(arrowIcon);
      selectContainer.appendChild(label);
      selectContainer.appendChild(selectWrapper);

      select.addEventListener("change", (event) => {
        onValueChange(event.target.value);
      });

      selectContainer.addEventListener("click", (event) => {
        if (event.target === select) return;

        const mousedown = new MouseEvent("mousedown", { bubbles: true });
        const mouseup = new MouseEvent("mouseup", { bubbles: true });
        select.dispatchEvent(mousedown);
        select.dispatchEvent(mouseup);
      });

      return selectContainer;
    },
    quantityInput: ({ title, value, onValueChange, name, unit = "" }) => {
      const quantityContainer = document.createElement("div");
      const input = document.createElement("input");
      input.name = name;
      const label = document.createElement("span");
      const controlsContainer = document.createElement("div");
      const plusButton = document.createElement("button");
      const division = document.createElement("div");
      const minusButton = document.createElement("button");
      const unitSpan = document.createElement("span");

      label.textContent = title;
      input.type = "number";
      input.value = value;
      input.min = "0";
      input.step = "1";
      unitSpan.textContent = unit;

      minusButton.type = "button";
      plusButton.type = "button";
      minusButton.textContent = "-";
      plusButton.textContent = "+";

      quantityContainer.classList.add(
        "loop-form-input-container",
        "loop-quantity-container",
      );
      label.classList.add("loop-form-input-label");
      input.classList.add("loop-form-input", "loop-quantity-input");
      controlsContainer.classList.add("loop-quantity-controls");
      plusButton.classList.add("loop-quantity-btn", "loop-quantity-plus");
      division.classList.add("loop-form-division");
      minusButton.classList.add("loop-quantity-btn", "loop-quantity-minus");
      unitSpan.classList.add("loop-quantity-unit");

      // Construir la estructura
      const inputWrapper = document.createElement("div");
      inputWrapper.classList.add("loop-quantity-input-wrapper");

      inputWrapper.appendChild(input);
      if (unit) {
        inputWrapper.appendChild(unitSpan);
      }
      inputWrapper.appendChild(controlsContainer);

      controlsContainer.appendChild(plusButton);
      controlsContainer.appendChild(division);
      controlsContainer.appendChild(minusButton);

      quantityContainer.appendChild(label);
      quantityContainer.appendChild(inputWrapper);

      // Event listeners
      input.addEventListener("input", (event) => {
        const newValue = Math.max(0, parseInt(event.target.value) || 0);
        input.value = newValue;
        onValueChange(newValue);
      });

      minusButton.addEventListener("click", (event) => {
        event.preventDefault();
        const currentValue = parseInt(input.value) || 0;
        const newValue = Math.max(0, currentValue - 1);
        input.value = newValue;
        onValueChange(newValue);
      });

      plusButton.addEventListener("click", (event) => {
        event.preventDefault();
        const currentValue = parseInt(input.value) || 0;
        const newValue = currentValue + 1;
        input.value = newValue;
        onValueChange(newValue);
      });

      quantityContainer.addEventListener("click", (event) => {
        if (!event.target.classList.contains("loop-quantity-btn")) {
          input.focus();
        }
      });

      return quantityContainer;
    },
    checkboxInput: ({ title, checked = false, onValueChange, name, id }) => {
      const checkboxContainer = document.createElement("div");
      const input = document.createElement("input");
      const label = document.createElement("label");
      const checkboxVisual = document.createElement("div");

      input.type = "checkbox";
      input.checked = checked;
      input.name = name;
      if (id) {
        input.id = id;
        label.setAttribute("for", id);
      }

      label.textContent = title;

      checkboxContainer.classList.add(
        "loop-form-input-container",
        "loop-checkbox-container",
      );
      input.classList.add("loop-form-checkbox");
      label.classList.add("loop-form-checkbox-label");
      checkboxVisual.classList.add("loop-checkbox-visual");

      // Update visual state
      const updateVisual = () => {
        if (input.checked) {
          checkboxVisual.classList.add("checked");
          checkboxVisual.innerHTML = window.LoopSVG.checkmark();
        } else {
          checkboxVisual.classList.remove("checked");
          checkboxVisual.innerHTML = "";
        }
      };

      // Initial visual state
      updateVisual();

      checkboxContainer.appendChild(label);
      checkboxContainer.appendChild(checkboxVisual);
      checkboxContainer.appendChild(input);

      // Event listener
      input.addEventListener("change", (event) => {
        updateVisual();
        onValueChange(event.target.checked);
      });

      // Click en el label también cambia el checkbox
      label.addEventListener("click", (event) => {
        event.preventDefault();
        input.checked = !input.checked;
        updateVisual();
        onValueChange(input.checked);
      });

      // Click en el checkbox visual también cambia el estado
      checkboxVisual.addEventListener("click", (event) => {
        event.preventDefault();
        input.checked = !input.checked;
        updateVisual();
        onValueChange(input.checked);
      });

      return checkboxContainer;
    },
  },
};

// --- Spinner component + helpers (append this after your current window.LoopUI) ---
(function () {
  // inject styles once
  function ensureSpinnerStyles() {
    if (document.getElementById("loopui-spinner-styles")) return;
    const style = document.createElement("style");
    style.id = "loopui-spinner-styles";
    style.textContent = `
      @keyframes loopui-spin { to { transform: rotate(360deg) } }
      .loopui-spinner{display:inline-block;width:1em;height:1em;border:2px solid transparent;border-top-color:currentColor;border-radius:50%;animation:loopui-spin .8s linear infinite}
      .loopui-spinner--sm{font-size:14px}
      .loopui-spinner--md{font-size:18px}
      .loopui-spinner--lg{font-size:22px}
      .loopui-btn-loading{pointer-events:none;opacity:.7}
      .loopui-overlay-spinner{position:fixed;inset:0;z-index:9999}
      .loopui-overlay-spinner__backdrop{position:absolute;inset:0;background:#fff;opacity:.4}
      .loopui-overlay-spinner__center{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    `;
    document.head.appendChild(style);
  }

  window.LoopUI.spinners = {
    inline(size = "md") {
      ensureSpinnerStyles();
      const span = document.createElement("span");
      span.className = `loopui-spinner loopui-spinner--${size}`;
      span.setAttribute("aria-hidden", "true");
      return span;
    },
    overlay() {
      ensureSpinnerStyles();
      const wrap = document.createElement("div");
      wrap.className = "loopui-overlay-spinner";
      wrap.innerHTML = `
        <div class="loopui-overlay-spinner__backdrop"></div>
        <div class="loopui-overlay-spinner__center">
          <span class="loopui-spinner loopui-spinner--lg" aria-hidden="true"></span>
        </div>
      `;
      document.body.appendChild(wrap);
      return { remove: () => wrap.remove() };
    },
  };

  // helper to run async actions with a button spinner
  window.LoopUI.helpers.withButtonSpinner = async (buttonEl, fn) => {
    ensureSpinnerStyles();
    if (!buttonEl) return fn();
    const prevHTML = buttonEl.innerHTML;
    const prevDisabled = buttonEl.disabled;

    buttonEl.disabled = true;
    buttonEl.classList.add("loopui-btn-loading");
    buttonEl.innerHTML = `<span class="loopui-spinner loopui-spinner--sm" aria-hidden="true"></span>`;

    try {
      return await fn();
    } finally {
      buttonEl.innerHTML = prevHTML;
      buttonEl.disabled = prevDisabled;
      buttonEl.classList.remove("loopui-btn-loading");
    }
  };
})();
